import { NestedUser, Database, NestedAgency } from '@habity/types';
import { getFullName } from '@habity/services';
import { hasProvider, getProviderSettings } from '@habity/agencies';
import {
  CustomerIOConfig,
  GoogleAnalyticsConfig,
  PostHogConfig,
  VitecAnalyticsConfig,
} from '@habity/types';

export type TrackingUserData = Partial<
  Database['public']['Tables']['users']['Row']
> & {
  homes: number;
  id_verified: boolean;
  environment: string;
  roles?: string;
}

export type TrackingProvider = {
  name: string;
  displayName: string;
  isActive: boolean;
  configKey?: string;
  status: 'active' | 'inactive' | 'no-config';
  description: string;
};

/**
 * Transforms a NestedUser into TrackingUserData format
 * This is the single source of truth for user data transformation
 * Used by both useTracking.identify() and the events page
 */
export const transformUserToTrackingData = (user: NestedUser): TrackingUserData => {
  return {
    id: user.id,
    email: user.email,
    phone: user.phone,
    avatar_url: user.avatar_url,
    homes: user?.homes?.length || 0,
    first_name: user.first_name,
    last_name: user.last_name,
    name: getFullName(user),
    created_at: user.created_at?.toString() || undefined,
    updated_at: user.updated_at?.toString() || undefined,
    id_verified: !!user.id_verified_at,
    id_verified_at: user.id_verified_at?.toString() || undefined,
    app_mode: user.app_mode,
    roles: user.roles?.join(', ') || undefined,
    terms_accepted_at: user.terms_accepted_at?.toString() || undefined,
    provider: user.provider,
    provider_id: user.provider_id,
    loan_promise: user.loan_promise,
    loan_promise_expires_at: user.loan_promise_expires_at?.toString() || undefined,
    source: user.source,
    // Address fields
    address_street: user.address_street,
    address_zip: user.address_zip,
    address_city: user.address_city,
    address_country: user.address_country,
    environment: process.env.NODE_ENV || 'unknown',
  };
};

/**
 * Get the status of all tracking providers for an agency
 */
export const getTrackingProvidersStatus = (agency: NestedAgency | null): TrackingProvider[] => {
  if (!agency) {
    return [
      {
        name: 'june',
        displayName: 'June Analytics',
        isActive: false,
        status: 'no-config',
        description: 'Product analytics and user behavior tracking'
      },
      {
        name: 'customer_io',
        displayName: 'Customer.io',
        isActive: false,
        status: 'no-config',
        description: 'Email marketing and customer messaging'
      },
      {
        name: 'google_analytics',
        displayName: 'Google Analytics',
        isActive: false,
        status: 'no-config',
        description: 'Web analytics and user tracking'
      },
      {
        name: 'posthog',
        displayName: 'PostHog',
        isActive: false,
        status: 'no-config',
        description: 'Product analytics and feature flags'
      },
    ];
  }

  const providers: TrackingProvider[] = [];

  // Customer.io
  const hasCustomerIO = hasProvider(agency, 'customer_io');
  const customerIOConfig = hasCustomerIO ? getProviderSettings(agency, 'customer_io') as CustomerIOConfig : null;
  providers.push({
    name: 'customer_io',
    displayName: 'Customer.io',
    isActive: !!(hasCustomerIO && customerIOConfig?.writeKey),
    configKey: customerIOConfig?.writeKey ? `${customerIOConfig.writeKey.substring(0, 8)}...` : undefined,
    status: hasCustomerIO ? (customerIOConfig?.writeKey ? 'active' : 'no-config') : 'inactive',
    description: 'Email marketing and customer messaging'
  });

  // Google Analytics
  const hasGA = hasProvider(agency, 'google_analytics');
  const gaConfig = hasGA ? getProviderSettings(agency, 'google_analytics') as GoogleAnalyticsConfig : null;
  providers.push({
    name: 'google_analytics',
    displayName: 'Google Analytics',
    isActive: !!(hasGA && gaConfig?.gaId),
    configKey: gaConfig?.gaId || undefined,
    status: hasGA ? (gaConfig?.gaId ? 'active' : 'no-config') : 'inactive',
    description: 'Web analytics and user tracking'
  });

  // PostHog
  const hasPostHog = hasProvider(agency, 'posthog');
  const posthogConfig = hasPostHog ? getProviderSettings(agency, 'posthog') as PostHogConfig : null;
  providers.push({
    name: 'posthog',
    displayName: 'PostHog',
    isActive: !!(hasPostHog && posthogConfig?.apiKey),
    configKey: posthogConfig?.apiKey ? `${posthogConfig.apiKey.substring(0, 8)}...` : undefined,
    status: hasPostHog ? (posthogConfig?.apiKey ? 'active' : 'no-config') : 'inactive',
    description: 'Product analytics and feature flags'
  });

  // Vitec Analytics
  const hasVitecAnalytics = hasProvider(agency, 'vitec_analytics');
  const vitecAnalyticsConfig = hasVitecAnalytics ? getProviderSettings(agency, 'vitec_analytics') as VitecAnalyticsConfig : null;
  providers.push({
    name: 'vitec_analytics',
    displayName: 'Vitec Analytics',
    isActive: !!(hasVitecAnalytics && vitecAnalyticsConfig?.apiKey),
    configKey: vitecAnalyticsConfig?.apiKey ? `${vitecAnalyticsConfig.apiKey.substring(0, 8)}...` : undefined,
    status: hasVitecAnalytics ? (vitecAnalyticsConfig?.apiKey ? 'active' : 'no-config') : 'inactive',
    description: 'Vitec estate page view tracking'
  });

  return providers;
};
